import React, { useState, useEffect } from 'react';
import { 
  HiOutlinePlus, 
  HiOutlineCheck, 
  HiOutlineTrash, 
  HiOutlinePencil, 
  HiOutlineX,
  HiOutlinePhotograph,
  HiOutlineArrowSmRight
} from 'react-icons/hi';
import toastService from '../../../services/toast';
import reviewService from '../../../services/review';
import Modal from '../../common/Modal';
import { Switch } from '@headlessui/react';
import { usePermissions } from '../../../contexts/PermissionsContext';

const DEFAULT_CUSTOMER_IMAGE = '/assets/default/default-avatar.png';

const CustomerReviews = () => {
  const { hasPermission } = usePermissions();
  const [reviews, setReviews] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [reviewType, setReviewType] = useState('text'); // 'text' or 'image'
  const [reviewText, setReviewText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerImage, setCustomerImage] = useState(DEFAULT_CUSTOMER_IMAGE);
  const [rating, setRating] = useState(5);
  const [isNameRequired, setIsNameRequired] = useState(true);
  const [isRatingRequired, setIsRatingRequired] = useState(true);
  const [showCustomerImage, setShowCustomerImage] = useState(true);  // صورة العميل مفعلة افتراضياً
  const [selectedImage, setSelectedImage] = useState(null);
  const [content, setContent] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);  // اسم العميل مغلق افتراضياً

  // جلب التعليقات عند تحميل الصفحة
  useEffect(() => {
    fetchReviews();
  }, []);

  // دالة جلب التعليقات
  const fetchReviews = async () => {
    try {
      const data = await reviewService.getAllReviews();
      setReviews(data);
    } catch (error) {
      toastService.error('حدث خطأ أثناء جلب التعليقات');
    }
  };

  useEffect(() => {
    if (selectedReview && isEditing) {
      setCustomerName(selectedReview.customer_name || '');
      setContent(selectedReview.content || '');
      setPreviewImage(selectedReview.type === 'image' ? selectedReview.content : '');
      setSelectedFile(null);
      setRating(selectedReview.rating || 5);
      setReviewType(selectedReview.type || 'text');
      
      // تحديث حالة اسم وصورة العميل
      setIsEnabled(!!selectedReview.customer_name);
      setShowCustomerImage(!!selectedReview.customer_image);
      setCustomerImage(selectedReview.customer_image || DEFAULT_CUSTOMER_IMAGE);
    } else {
      resetForm();
    }
  }, [selectedReview, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerName.trim() && isEnabled) {
      toastService.error('يجب إدخال اسم العميل');
      return;
    }

    if (reviewType === 'text' && (!content.trim() || content.length < 10)) {
      toastService.error('يجب إدخال نص التقييم (10 أحرف على الأقل)');
      return;
    }

    if (reviewType === 'image' && !selectedFile && !content) {
      toastService.error('يجب اختيار صورة للتقييم');
      return;
    }

    const reviewData = new FormData();
    reviewData.append('type', reviewType);
    
    // إذا كان التقييم نصي
    if (reviewType === 'text') {
      reviewData.append('content', content);
    } 
    // إذا كان التقييم صورة
    else {
      // إذا كان هناك ملف جديد تم اختياره
      if (selectedFile) {
        reviewData.append('content', selectedFile);
      } 
      // إذا كنا نعدل تقييم موجود ولم نغير الصورة
      else if (content) {
        reviewData.append('content', content);
      }
    }

    if (isEnabled) {
      reviewData.append('customer_name', customerName.trim());
    } else {
      reviewData.append('customer_name', '');
    }

    if (showCustomerImage) {
      reviewData.append('customer_image', DEFAULT_CUSTOMER_IMAGE);
    } else {
      reviewData.append('customer_image', '');
    }

    if (rating) {
      reviewData.append('rating', rating);
    }

    try {
      let response;
      if (isEditing && selectedReview) {
        reviewData.append('_method', 'PUT');
        response = await reviewService.updateReview(selectedReview.id, reviewData);
      } else {
        response = await reviewService.createReview(reviewData);
      }

      await fetchReviews();
      setIsAddModalOpen(false);
      resetForm();
      toastService.success(isEditing ? 'تم تحديث التقييم بنجاح' : 'تم إضافة التقييم بنجاح');
    } catch (error) {
      toastService.error('حدث خطأ أثناء ' + (isEditing ? 'تحديث' : 'إضافة') + ' التقييم');
    }
  };

  const handleDelete = async (review) => {
    try {
      await reviewService.deleteReview(review.id);
      toastService.success('تم حذف التقييم بنجاح');
      fetchReviews(); // تحديث القائمة
    } catch (error) {
      toastService.error('حدث خطأ أثناء حذف التقييم');
    }
  };

  const resetForm = () => {
    setReviewType('text');
    setContent('');
    setSelectedFile(null);
    setPreviewImage(null);
    setCustomerName('');
    setCustomerImage(DEFAULT_CUSTOMER_IMAGE);
    setRating(5);
    setIsAddModalOpen(false);
    setSelectedReview(null);
    setIsEditing(false);
    setIsEnabled(false);  // اسم العميل مغلق افتراضياً
    setShowCustomerImage(true);  // صورة العميل مفعلة افتراضياً
  };

  const handleImageChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        toastService.error('حجم الصورة يجب أن يكون أقل من 10MB');
        return;
      }
      setSelectedFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);
      setContent(previewUrl); // نضيف هذا السطر عشان يظهر المعاينة
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    // نستنى شوية قبل ما نعمل reset للفورم
    setTimeout(() => {
      setReviewType('text');
      setContent('');
      setSelectedFile(null);
      setPreviewImage(null);
      setCustomerName('');
      setCustomerImage(DEFAULT_CUSTOMER_IMAGE);
      setRating(5);
      setSelectedReview(null);
      setIsEditing(false);
      setIsEnabled(false);
      setShowCustomerImage(true);
    }, 300);
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">تقييمات العملاء</h2>
          {hasPermission && hasPermission('create_review') && (
            <div className="relative group">
              <HiOutlinePlus 
                className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                onClick={() => setIsAddModalOpen(true)}
              />
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                إضافة تقييم جديد
              </div>
            </div>
          )}
        </div>

        <div className="p-6">
          {/* محتوى البطاقة */}
          <div className="space-y-4">
            {/* التقييمات النصية */}
            <div>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-blue-200 dark:border-blue-800"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white dark:bg-gray-800 px-4 text-lg font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2">
                    <HiOutlinePencil className="w-5 h-5" />
                    التقييمات النصية
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-5xl">
                {reviews
                  .filter(review => review.type === 'text')
                  .map(review => (
                    <div
                      key={review.id}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 h-[116px] overflow-hidden"
                    >
                      {/* معلومات العميل */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* صورة العميل */}
                          {review.customer_image && (
                            <div className="shrink-0">
                              <img
                                src={review.customer_image}
                                alt={review.customer_name || 'صورة العميل'}
                                className="w-10 h-10 rounded-full"
                              />
                            </div>
                          )}

                          {/* اسم العميل */}
                          <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                            {review.customer_name}
                          </span>
                        </div>

                        {/* أزرار التحكم */}
                        <div className="flex items-center gap-0.5 shrink-0">
                          {hasPermission && hasPermission('edit_review') && (
                            <div className="relative group">
                              <button
                                onClick={() => {
                                  setSelectedReview(review);
                                  setIsEditing(true);
                                  setIsAddModalOpen(true);
                                }}
                                className="p-2 text-blue-600 dark:text-blue-400 rounded-full"
                              >
                                <HiOutlinePencil className="w-5 h-5" />
                              </button>
                              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                تعديل التقييم
                              </div>
                            </div>
                          )}
                          {hasPermission && hasPermission('delete_review') && (
                            <div className="relative group">
                              <button
                                onClick={() => handleDelete(review)}
                                className="p-2 text-red-600 dark:text-red-400 rounded-full"
                              >
                                <HiOutlineTrash className="w-5 h-5" />
                              </button>
                              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                حذف التقييم
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* التقييم والتاريخ */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, index) => (
                            <span
                              key={index}
                              className={`text-yellow-400 text-xs ${index < review.rating ? '' : 'text-gray-300 dark:text-gray-600'}`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* محتوى التقييم */}
                      <div className="flex-1 overflow-hidden">
                        <p className="text-gray-600 dark:text-gray-300 text-sm overflow-hidden text-ellipsis whitespace-nowrap">
                          {review.content}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* التقييمات المصورة */}
            <div className="mt-8">
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-blue-200 dark:border-blue-800"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white dark:bg-gray-800 px-4 text-lg font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2">
                    <HiOutlinePencil className="w-5 h-5" />
                    التقييمات المصورة
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-5xl">
                {reviews
                  .filter(review => review.type === 'image')
                  .map(review => (
                    <div
                      key={review.id}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 flex gap-3 items-start h-[116px] overflow-hidden"
                    >
                      {/* صورة التقييم */}
                      <div 
                        className="relative group cursor-pointer rounded-lg overflow-hidden shrink-0"
                        onClick={() => setSelectedImage(review.content)}
                      >
                        <img
                          src={review.content}
                          alt="تقييم العميل"
                          className="w-[80px] h-[80px] object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-2 py-1 bg-black/50 rounded">
                            عرض الصورة
                          </div>
                        </div>
                      </div>

                      {/* معلومات التقييم */}
                      <div className="flex-1 min-w-0">
                        {/* معلومات العميل */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {/* صورة العميل */}
                            {review.customer_image && (
                              <div className="shrink-0">
                                <img
                                  src={review.customer_image}
                                  alt={review.customer_name || 'صورة العميل'}
                                  className="w-10 h-10 rounded-full"
                                />
                              </div>
                            )}

                            {/* اسم العميل */}
                            <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                              {review.customer_name}
                            </span>
                          </div>

                          {/* أزرار التحكم */}
                          <div className="flex items-center gap-0.5 shrink-0">
                            <div className="relative group">
                              <button
                                onClick={() => {
                                  setSelectedReview(review);
                                  setIsEditing(true);
                                  setIsAddModalOpen(true);
                                }}
                                className="p-2 text-blue-600 dark:text-blue-400 rounded-full"
                              >
                                <HiOutlinePencil className="w-5 h-5" />
                              </button>
                              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                تعديل التقييم
                              </div>
                            </div>
                            <div className="relative group">
                              <button
                                onClick={() => handleDelete(review)}
                                className="p-2 text-red-600 dark:text-red-400 rounded-full"
                              >
                                <HiOutlineTrash className="w-5 h-5" />
                              </button>
                              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                                حذف التقييم
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* التقييم */}
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, index) => (
                              <span
                                key={index}
                                className={`text-yellow-400 text-xs ${index < review.rating ? '' : 'text-gray-300 dark:text-gray-600'}`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={handleCloseModal}
          title={isEditing ? "تعديل تقييم" : "إضافة تقييم جديد"}
          actions={
            <button
              onClick={handleSubmit}
              className="text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
              title="حفظ"
            >
              <HiOutlineCheck className="w-6 h-6" />
            </button>
          }
        >
          <div className="space-y-6">
            {/* الصف الأول: اسم العميل والتفعيل */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  اسم العميل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required={isEnabled}
                  disabled={!isEnabled}
                  className={`input-base ${!isEnabled && 'opacity-50 cursor-not-allowed'}`}
                  placeholder="أدخل اسم العميل"
                />
              </div>
              <div className="pt-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isEnabled}
                    onChange={setIsEnabled}
                    className={`switch-rtl ${
                      isEnabled ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                  >
                    <span
                      className={`${
                        isEnabled ? '-translate-x-6' : '-translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                  <span className={`text-sm ${isEnabled ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>
                    تفعيل
                  </span>
                </div>
              </div>
            </div>

            {/* الصف الثاني: نوع التقييم والمحتوى */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={reviewType === 'image'}
                    onChange={(checked) => setReviewType(checked ? 'image' : 'text')}
                    className={`switch-rtl ${
                      reviewType === 'image' ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                  >
                    <span
                      className={`${
                        reviewType === 'image' ? '-translate-x-6' : '-translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {reviewType === 'text' ? 'نص' : 'صورة'}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showCustomerImage}
                      onChange={setShowCustomerImage}
                      className={`switch-rtl ${
                        showCustomerImage ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                    >
                      <span
                        className={`${
                          showCustomerImage ? '-translate-x-6' : '-translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                    <span className="text-sm text-gray-700 dark:text-gray-200">
                      إظهار صورة العميل
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <img
                      src={customerImage}
                      alt="صورة افتراضية"
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {reviewType === 'text' ? (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  minLength={10}
                  maxLength={500}
                  className="input-base resize-none h-[129px] w-full"
                  placeholder="أدخل نص التقييم (10 أحرف على الأقل)"
                />
              ) : (
                <label 
                  htmlFor="file-upload"
                  className="cursor-pointer block">
                  <div className="flex justify-center items-center rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 h-[129px] relative">
                    {previewImage ? (
                      <>
                        <img 
                          src={previewImage} 
                          alt="معاينة الصورة" 
                          className="absolute inset-0 w-full h-full object-contain p-2"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <p className="text-white text-sm">انقر لتغيير الصورة</p>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-1 text-center">
                        <HiOutlinePhotograph className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          <span className="text-blue-600 dark:text-blue-500 hover:text-blue-500 dark:hover:text-blue-400">رفع صورة</span>
                          <p className="mt-1">أو اسحب وأفلت</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG حتى 10MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              )}
            </div>

            {/* الصف الثالث: التقييم بالنجوم */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                التقييم بالنجوم
              </label>
              <select
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                className="input-base"
              >
                <option value="5">5 نجوم</option>
                <option value="4">4 نجوم</option>
                <option value="3">3 نجوم</option>
                <option value="2">نجمتين</option>
                <option value="1">نجمة واحدة</option>
              </select>
            </div>
          </div>
        </Modal>

        {/* عرض الصورة المكبرة */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative" onClick={e => e.stopPropagation()}>
              <img
                src={selectedImage}
                alt="تقييم العميل"
                className="max-h-[80vh] w-auto rounded-lg"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-2 left-2 p-1.5 bg-gray-900/50 hover:bg-gray-900/70 text-white rounded-full transition-colors duration-200"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerReviews;
